"use client";

import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import Button from "@/components/library/Button";
import Icon from "@/components/library/Icon";
import { Combobox } from "@/components/ui/combobox";

interface ScheduleCardProps {
  readonly title: string;
  readonly description: string;
  readonly daysActive: string;
  readonly timesActive: string;
  readonly daysActiveOptions: { value: string; label: string }[];
  readonly timesActiveOptions: { value: string; label: string }[];
  readonly onDaysActiveChange: (value: string) => void;
  readonly onTimesActiveChange: (value: string) => void;
  readonly hoursSummary: string;
  readonly summaryLabel: string;
  readonly showCustomOptions: boolean;
  readonly onEditClick: () => void;
  readonly summaryColorClass?: string;
  readonly children?: ReactNode;
}

export function ScheduleCard({
  title,
  description,
  daysActive,
  timesActive,
  daysActiveOptions,
  timesActiveOptions,
  onDaysActiveChange,
  onTimesActiveChange,
  hoursSummary,
  summaryLabel,
  showCustomOptions,
  onEditClick,
  summaryColorClass = "bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300",
  children,
}: ScheduleCardProps) {
  const { t } = useTranslation("onboarding");

  return (
    <div className="rounded-2xl border border-200 bg-panel p-6 space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-foreground">{title}</h3>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>

      {/* Dropdowns Row */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground">
            {t("businessHours.daysActive")}
          </label>
          <Combobox
            options={daysActiveOptions}
            value={daysActive}
            onChange={onDaysActiveChange}
            triggerClassName="w-full"
          />
        </div>
        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground">
            {t("businessHours.timesActive")}
          </label>
          <Combobox
            options={timesActiveOptions}
            value={timesActive}
            onChange={onTimesActiveChange}
            triggerClassName="w-full"
          />
        </div>
      </div>

      {/* Hours Summary Info Bar */}
      <div
        className={`flex items-center justify-between rounded-xl px-4 py-3 ${summaryColorClass}`}
      >
        <div className="flex items-start gap-2 text-sm">
          <Icon icon="circle-info" className="w-4 h-4 self-center" />
          <span className="leading-5">
            <span className="block">{summaryLabel}:</span>
            <span className="block font-semibold">{hoursSummary}</span>
          </span>
        </div>
        {!showCustomOptions && (
          <Button type="tertiary" compact onClick={onEditClick}>
            {t("businessHours.edit")}
          </Button>
        )}
      </div>

      {/* Custom Options (injected as children) */}
      {children}
    </div>
  );
}
